/**
 * IPAMIPs — /networks/ips (estándar de listados del CMDB).
 *
 * Cada fila es una dirección IP registrada en el IPAM: su `ip`, su subred, su
 * `status` (libre / reservada / asignada / conflicto) y su `type` (estática,
 * DHCP, o IP virtual de un grupo CARP/VRRP/HSRP). La columna «Asignado a» sale
 * de cruzar la IP con `ipam_ip_assignments` y el catálogo de CIs.
 *
 * Antes: tabla propia de solo lectura con un `SortTh` local, buscador y tres
 * selects, alta embebida y sin editar ni borrar (el backend solo tenía el POST;
 * ahora tiene también PUT y DELETE `/ipam/ips/<id>`).
 *
 * Ahora: `ListPage` da buscador, filtros, columnas, orden, cebra, vistas y
 * export; los KPIs de estado son chips que filtran al pulsarlos (como en
 * Estaciones de Trabajo) y la ficha entra en lectura, con Editar y Eliminar.
 *
 * Botones propios: **Ver el equipo asignado** (lleva a la ficha de ese CI con
 * `?destacar=`, la convención del CMDB) y **Ver en el Explorer** (mapa de IPs de
 * su subred).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CircleStackIcon,
  ListBulletIcon,
  Squares2X2Icon,
  MagnifyingGlassIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import IPAMLayout from './IPAMLayout';
import ListPage from './common/ListPage';
import api from '../services/api';
import { compareText } from '../utils/ipamSort';
import { useListView } from '../views/ViewsContext';
import { docElementPath } from '../utils/docElements';
import { assignmentName, ciTypeLabel } from '../utils/ipamCi';
import '../styles/DetailView.css';   // `.dv-fieldset` (la ficha se apaga de golpe en lectura)

const STATUS_META = {
  free:     { label: 'Libre',     badge: 'list-badge--on' },
  assigned: { label: 'Asignada',  badge: 'list-badge--off' },
  reserved: { label: 'Reservada', badge: 'list-badge--warn' },
  conflict: { label: 'Conflicto', badge: 'list-badge' },
};

const TYPE_META = {
  static:  { label: 'Estática' },
  dhcp:    { label: 'DHCP' },
  vip:     { label: 'VIP' },
  vrrp:    { label: 'VRRP' },
  hsrp:    { label: 'HSRP' },
  virtual: { label: 'Virtual' },
};
function ipNum(ip) {
  const parts = String(ip || '').split('.');
  if (parts.length !== 4) return 0;
  return parts.reduce((acc, o) => acc * 256 + Number(o), 0);
}

export default function IPAMIPs({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const [ips, setIps]               = useState([]);
  const [subnets, setSubnets]       = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [ciCatalog, setCiCatalog]   = useState({});

  const [search, setSearch]           = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType]   = useState('');
  const [filterSubnet, setFilterSubnet] = useState(searchParams.get('subnetId') || '');
  const [filterSite, setFilterSite]   = useState('');
  const [filterAssigned, setFilterAssigned] = useState('');
  const [filterDns, setFilterDns]     = useState('');
  const [filterComments, setFilterComments] = useState('');
  const [hlId, setHlId]               = useState(null);
  const [knownSites, setKnownSites]   = useState([]);

  /* ficha / alta */
  const [formOpen, setFormOpen]   = useState(false);
  const [initialMode, setInitialMode] = useState('create');
  const [editingIp, setEditingIp] = useState(null);
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 9000);
    return () => clearTimeout(timer);
  }, [success]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [i, s, a, srv, elems, ws, printers, phones] = await Promise.allSettled([
        api.getIpamIPs(companyId, { limit: 1000 }),
        api.getIpamSubnets(companyId),
        api.getIpamAssignments(companyId),
        api.getServers(companyId),
        api.getElements(companyId),
        api.getWorkstations({ company_id: companyId }),
        api.getPrinters({ company_id: companyId }),
        api.getIPPhones(companyId),
      ]);
      const ex = r => r.status === 'fulfilled' ? (r.value?.data || []) : [];
      setIps(ex(i));
      setSubnets(ex(s));
      setAssignments(ex(a));
      const catalog = {};
      const servers = ex(srv);
      [...servers.map(x => ({ ...x, _type: 'server' })),
       ...servers.map(x => ({ ...x, _type: 'server_team' })),
       ...ex(elems).map(x => ({ ...x, _type: 'rack_element' })),
       ...ex(ws).map(x => ({ ...x, _type: 'workstation' })),
       ...ex(printers).map(x => ({ ...x, _type: 'printer' })),
       ...ex(phones).map(x => ({ ...x, _type: 'ip_phone' })),
      ].forEach(item => {
        const key = `${item._type}:${item.id}`;
        const n = item.name || item.code || item.hostname || `#${item.id}`;
        catalog[key] = item._type === 'rack_element' ? `[${item.element_type}] ${n}` : n;
      });
      setCiCatalog(catalog);
    } catch (e) {
      setError(e.message || 'Error cargando IPs');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  // Memoizado a propósito: de aquí sale `siteOf`, del que depende un efecto que
  // hace setState. Si el objeto cambia de identidad en cada render, el efecto se
  // repite sin parar (React avisa de «Maximum update depth exceeded»).
  const subnetMap = useMemo(
    () => Object.fromEntries(subnets.map(s => [String(s.id), s])),
    [subnets],
  );

  /* map: ip_address_string → first active assignment */
  const assignmentByIp = useMemo(() => {
    const m = {};
    for (const a of assignments) {
      if (!m[a.ip]) m[a.ip] = a;  // first assignment wins (primary usually)
    }
    return m;
  }, [assignments]);

  const siteOf = useCallback(
    (ip) => (ip?.subnet_id != null ? (subnetMap[String(ip.subnet_id)]?.site || '') : ''),
    [subnetMap],
  );

  useEffect(() => {
    const sites = new Set();
    ips.forEach(row => { const s = siteOf(row); if (s) sites.add(s); });
    if (!sites.size) return;
    // Solo se actualiza si hay sitios nuevos: devolver `prev` evita renders
    // en cadena (el efecto depende de `ips` y `siteOf`).
    setKnownSites(prev => {
      const nuevos = [...sites].filter(s => !prev.includes(s));
      if (!nuevos.length) return prev;
      return [...prev, ...nuevos].sort((a, b) => compareText(a, b));
    });
  }, [ips, siteOf]);

  const asgnNameOf = useCallback(
    (asgn) => assignmentName(asgn, ciCatalog),
    [ciCatalog],
  );

  /** Ruta a la ficha del equipo al que está asignada la IP (o null). */
  const assignedPathOf = useCallback((asgn) => {
    if (!asgn) return null;
    const type = asgn.object_type === 'server_team' ? 'server' : asgn.object_type;
    return docElementPath(type, asgn.object_id);
  }, []);

  /* ── ficha (lectura por defecto) ─────────────────────────────────────── */
  const openView = (ip) => { setEditingIp(ip); setInitialMode('view'); setFormOpen(true); };
  const openEdit = (ip) => { setEditingIp(ip); setInitialMode('edit'); setFormOpen(true); };
  const openCreate = () => { setEditingIp(null); setInitialMode('create'); setFormOpen(true); };

  const closeForm = () => {
    setFormOpen(false);
    setEditingIp(null);
  };

  const handleSave = async (data) => {
    setSaving(true);
    try {
      if (editingIp && initialMode === 'edit') {
        await api.updateIpamIP(editingIp.id, {
          type: data.type,
          status: data.status,
          dns_name: data.dns_name || null,
          comments: data.comments || null,
        }, companyId);
        setSuccess(`IP ${editingIp.ip} actualizada.`);
      } else {
        await api.createIpamIP({
          ip: data.ip,
          subnet_id: data.subnet_id ? Number(data.subnet_id) : undefined,
          type: data.type,
          status: data.status,
          dns_name: data.dns_name || null,
          comments: data.comments || null,
        }, companyId);
        setSuccess(`IP ${data.ip} registrada.`);
      }
      closeForm();
      await load();
    } catch (e) {
      setError(e.message || 'Error guardando la IP');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ip) => {
    try {
      await api.deleteIpamIP(ip.id, companyId);
      await load();
    } catch (e) {
      setError(e.message || 'Error eliminando la IP');
    }
  };

  /* ── ?destacar / ?abrir y limpieza de ?subnetId ───────────────────────── */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !ips.length) return undefined;
    const id = Number(raw);
    const target = ips.find(row => row.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { openView(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`ip-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, ips, setSearchParams]);

  /* ── filtrado ─────────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return ips.filter((ip) => {
      const asgn = assignmentByIp[ip.ip];
      if (filterStatus && ip.status !== filterStatus) return false;
      if (filterType && ip.type !== filterType) return false;
      if (filterSubnet && String(ip.subnet_id) !== filterSubnet) return false;
      if (filterSite && siteOf(ip) !== filterSite) return false;
      if (filterAssigned === 'with' && !asgn) return false;
      if (filterAssigned === 'without' && asgn) return false;
      if (filterDns === 'with' && !ip.dns_name) return false;
      if (filterDns === 'without' && ip.dns_name) return false;
      if (filterComments === 'with' && !ip.comments) return false;
      if (filterComments === 'without' && ip.comments) return false;
      if (!needle) return true;
      return (ip.ip || '').includes(needle)
        || (ip.dns_name || '').toLowerCase().includes(needle)
        || (ip.comments || '').toLowerCase().includes(needle)
        || (subnetMap[String(ip.subnet_id)]?.cidr || '').toLowerCase().includes(needle)
        || asgnNameOf(asgn).toLowerCase().includes(needle)
        || (asgn?.assigned_by || '').toLowerCase().includes(needle);
    });
  }, [ips, search, filterStatus, filterType, filterSubnet, filterSite, filterAssigned,
      filterDns, filterComments, assignmentByIp, subnetMap, siteOf, asgnNameOf]);

  /* ── columnas (10 de catálogo; 6 visibles de entrada) ─────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    const dateOf = (asgn) => (asgn?.created_at
      ? new Date(asgn.created_at).toLocaleDateString('es-ES', {
        day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
      })
      : '');
    return [
      {
        key: 'ip',
        label: 'IP',
        sortable: true,
        sortValue: (ip) => ipNum(ip.ip),
        render: (ip) => <span className="list-code" style={{ fontWeight: 700 }}>{ip.ip}</span>,
      },
      {
        key: 'subnet_cidr',
        label: tr('ipam.subnet', 'Subred'),
        sortable: true,
        sortValue: (ip) => subnetMap[String(ip.subnet_id)]?.cidr || '',
        render: (ip) => {
          const subnet = subnetMap[String(ip.subnet_id)];
          if (!subnet) return dash;
          return (
            <button
              type="button"
              className="list-code"
              style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0 }}
              title={tr('ip.view_in_explorer', 'Ver en el Explorer')}
              onClick={(event) => { event.stopPropagation(); navigate(explorerPath(subnet)); }}
            >
              {subnet.cidr}
            </button>
          );
        },
      },
      {
        key: 'site',
        label: tr('ipam.site', 'Site'),
        sortable: true,
        sortValue: (ip) => siteOf(ip),
        render: (ip) => siteOf(ip) || dash,
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (ip) => ip.status || '',
        render: (ip) => {
          const meta = STATUS_META[ip.status] || STATUS_META.conflict;
          return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
        },
      },
      {
        key: 'type',
        label: tr('ipam.type', 'Tipo'),
        sortable: true,
        sortValue: (ip) => ip.type || 'static',
        render: (ip) => (
          <span className={`list-badge ${ip.type && ip.type !== 'static' ? 'list-badge--warn' : ''}`.trim()}>
            {(TYPE_META[ip.type] || {}).label || ip.type || 'Estática'}
          </span>
        ),
      },
      {
        key: 'assigned',
        label: tr('ip.assigned_to', 'Asignado a'),
        sortable: true,
        sortValue: (ip) => asgnNameOf(assignmentByIp[ip.ip]),
        render: (ip) => {
          const asgn = assignmentByIp[ip.ip];
          if (!asgn) return dash;
          const name = asgnNameOf(asgn);
          return (
            <span style={{ display: 'inline-flex', flexDirection: 'column', maxWidth: 200 }}>
              <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>
                {name}
              </span>
              <span className="list-hint">
                {ciTypeLabel(asgn.object_type)}
                {asgn.interface_ref ? ` · ${asgn.interface_ref}` : ''}
              </span>
            </span>
          );
        },
      },
      {
        key: 'assigned_at',
        label: tr('ip.assigned_at', 'Fecha asig.'),
        sortable: true,
        sortValue: (ip) => assignmentByIp[ip.ip]?.created_at || '',
        render: (ip) => dateOf(assignmentByIp[ip.ip]) || dash,
      },
      {
        key: 'assigned_by',
        label: tr('ip.assigned_by', 'Por'),
        sortable: true,
        sortValue: (ip) => assignmentByIp[ip.ip]?.assigned_by || '',
        render: (ip) => assignmentByIp[ip.ip]?.assigned_by || dash,
      },
      {
        key: 'dns_name',
        label: 'DNS',
        sortable: true,
        render: (ip) => (ip.dns_name ? <span className="list-code">{ip.dns_name}</span> : dash),
      },
      {
        key: 'comments',
        label: tr('ip.comments', 'Comentarios'),
        render: (ip) => ip.comments || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, subnetMap, siteOf, assignmentByIp, asgnNameOf, navigate]);

  const defaultVisible = ['ip', 'subnet_cidr', 'status', 'type', 'assigned', 'dns_name'];

  const filters = [
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ip.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'type', label: tr('ipam.type', 'Tipo'), allLabel: tr('ip.type_all', 'Todos los tipos'),
      value: filterType, onChange: setFilterType,
      options: Object.entries(TYPE_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'subnet', label: tr('ipam.subnet', 'Subred'), allLabel: tr('ip.subnet_all', 'Todas las subredes'),
      value: filterSubnet, onChange: setFilterSubnet,
      options: subnets.map(s => ({ value: String(s.id), label: s.cidr })),
    },
    {
      key: 'site', label: tr('ipam.site', 'Site'), allLabel: tr('ipam.site_all', 'Todos los sites'),
      value: filterSite, onChange: setFilterSite,
      options: knownSites.map(s => ({ value: s, label: s })),
    },
    {
      key: 'assigned', label: tr('ip.assigned', 'Asignación'), allLabel: tr('ip.assigned_all', 'Asignadas y libres'),
      value: filterAssigned, onChange: setFilterAssigned,
      options: [
        { value: 'with', label: tr('ip.only_assigned', 'Asignadas a un equipo') },
        { value: 'without', label: tr('ip.only_free', 'Sin asignar') },
      ],
    },
    {
      key: 'dns', label: 'DNS', allLabel: tr('ip.dns_all', 'Con y sin DNS'),
      value: filterDns, onChange: setFilterDns,
      options: [
        { value: 'with', label: tr('ip.dns_with', 'Con nombre DNS') },
        { value: 'without', label: tr('ip.dns_without', 'Sin nombre DNS') },
      ],
    },
    {
      key: 'comments', label: tr('ip.comments', 'Comentarios'), allLabel: tr('ip.comments_all', 'Con y sin comentarios'),
      value: filterComments, onChange: setFilterComments,
      options: [
        { value: 'with', label: tr('ip.comments_with', 'Con comentarios') },
        { value: 'without', label: tr('ip.comments_without', 'Sin comentarios') },
      ],
    },
  ];

  const subtitle = `${ips.length} ${tr('ip.plural', 'IPs')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtradas')}`;

  const exportRows = useMemo(() => filtered.map(ip => {
    const asgn = assignmentByIp[ip.ip];
    return {
      'IP': ip.ip,
      'Subred': subnetMap[String(ip.subnet_id)]?.cidr || '',
      'Site': siteOf(ip),
      'Estado': (STATUS_META[ip.status] || STATUS_META.conflict).label,
      'Tipo': (TYPE_META[ip.type] || {}).label || ip.type || '',
      'Asignado a': asgnNameOf(asgn),
      'Tipo de equipo': asgn ? ciTypeLabel(asgn.object_type) : '',
      'Interfaz': asgn?.interface_ref || '',
      'Fecha asignación': asgn?.created_at ? new Date(asgn.created_at).toLocaleString() : '',
      'Asignado por': asgn?.assigned_by || '',
      'DNS': ip.dns_name || '',
      'Comentarios': ip.comments || '',
    };
  }), [filtered, subnetMap, siteOf, assignmentByIp, asgnNameOf]);

  /* Ruta al Explorer de una subred (mapa de IPs). */
  function explorerPath(subnet) {
    if (!subnet) return '/networks/explorer';
    const params = new URLSearchParams();
    if (subnet.block_id != null) params.set('blockId', subnet.block_id);
    params.set('subnetId', subnet.id);
    return `/networks/explorer?${params.toString()}`;
  }

  /** Tarjetas: lo esencial de la IP, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((ip) => {
          const statusMeta = STATUS_META[ip.status] || STATUS_META.conflict;
          const asgn = assignmentByIp[ip.ip];
          const subnet = subnetMap[String(ip.subnet_id)];
          return (
            <div
              key={ip.id}
              id={`ip-card-${ip.id}`}
              className={`list-card${ip.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(ip)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(ip); }}
              title={`${tr('action.view', 'Ver')} ${ip.ip}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title list-code">{ip.ip}</div>
                  {ip.dns_name && <span className="list-code">{ip.dns_name}</span>}
                </div>
                <span className={`list-badge ${statusMeta.badge}`}>{statusMeta.label}</span>
              </div>

              <div className="list-card-meta">
                <span className={`list-badge ${ip.type && ip.type !== 'static' ? 'list-badge--warn' : ''}`.trim()}>
                  {(TYPE_META[ip.type] || {}).label || ip.type || 'Estática'}
                </span>
                {subnet && <span className="list-code">{subnet.cidr}</span>}
                {siteOf(ip) && <span>📍 {siteOf(ip)}</span>}
              </div>

              <div className="list-card-meta">
                {asgn
                  ? <span>{tr('ip.assigned_to', 'Asignado a')}: {asgnNameOf(asgn)} ({ciTypeLabel(asgn.object_type)})</span>
                  : <span className="list-hint">{tr('ip.free_hint', 'Sin asignar a ningún equipo')}</span>}
              </div>

              {ip.comments && <div className="list-card-meta"><span>{ip.comments}</span></div>}

              <div className="list-card-actions">{rowActions(ip)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const IP_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada IP'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId, assignmentByIp, subnetMap, siteOf]);
  useListView('ipam_ips', 'list');

  if (!companyId) {
    return (
      <IPAMLayout>
        <div className="alert alert-warning">{tr('ipam.select_company', 'Selecciona una empresa para ver el IPAM.')}</div>
      </IPAMLayout>
    );
  }

  /* Contadores por estado: alimentan los chips de KPI, que filtran al pulsarlos. */
  const statusCounts = ips.reduce((acc, ip) => {
    acc[ip.status] = (acc[ip.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <IPAMLayout>
      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between">
          <span>{error}</span>
          <button className="btn-close" onClick={() => setError('')} />
        </div>
      )}
      {success && (
        <div className="alert alert-success py-2 d-flex justify-content-between">
          <span>{success}</span>
          <button className="btn-close" onClick={() => setSuccess('')} />
        </div>
      )}

      <ListPage
        icon={CircleStackIcon}
        title={tr('ip.title', 'Direcciones IP')}
        subtitle={subtitle}

        onCreate={openCreate}
        createLabel={tr('ip.new', 'Registrar IP')}
        createTitle={tr('ip.new_hint', 'Registrar una IP suelta (la subred se deduce del propio IP)')}

        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={tr('ip.search', 'Buscar IP, DNS, subred, equipo, comentario…')}
        filters={filters}

        exportRows={exportRows}
        exportFileBase="ips"
        exportSheetName="Direcciones IP"

        columns={columns}
        columnStorageKey="cmdb_cols_ipam_ips"
        defaultVisible={defaultVisible}
        rows={filtered}
        rowKey={(ip) => ip.id}
        rowId={(ip) => `ip-row-${ip.id}`}
        rowClassName={(ip) => (ip.id === hlId ? 'list-row-highlight' : '')}
        onRowClick={openView}
        rowTitle={(ip) => `${tr('action.view', 'Ver')} ${ip.ip}`}

        views={IP_VIEWS}
        listKey="ipam_ips"
        defaultView="list"

        loading={loading}
        loadingText={tr('msg.loading', 'Cargando direcciones IP…')}

        /* Chips de estado: filtran al pulsarlos (y «Total» los quita todos). */
        kpis={(
          <div className="list-kpis list-kpis--chips">
            <button
              type="button"
              className={`list-kpi-chip${!filterStatus ? ' list-kpi-chip--on' : ''}`}
              onClick={() => setFilterStatus('')}
              title={tr('ip.kpi_clear', 'Quitar el filtro de estado')}
            >
              <span className="list-kpi-value">{ips.length}</span>
              <span className="list-kpi-label">{tr('ip.total', 'Total')}</span>
            </button>
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <button
                key={key}
                type="button"
                className={`list-kpi-chip${filterStatus === key ? ' list-kpi-chip--on' : ''}`}
                onClick={() => setFilterStatus(filterStatus === key ? '' : key)}
                title={`${tr('ip.kpi_filter', 'Filtrar por')} ${meta.label}`}
              >
                <span className="list-kpi-value">{statusCounts[key] || 0}</span>
                <span className="list-kpi-label">{meta.label}</span>
              </button>
            ))}
          </div>
        )}

        empty={{
          icon: '🔢',
          title: ips.length === 0
            ? tr('ip.empty', 'No hay direcciones IP registradas')
            : tr('msg.no_results', 'Ninguna IP coincide con los filtros'),
          subtitle: ips.length === 0
            ? tr('ip.empty_hint', 'Se dan de alta solas al asignar IPs desde las fichas, o regístralas aquí.')
            : undefined,
          action: ips.length === 0 ? (
            <button type="button" className="list-btn-primary" onClick={openCreate}>
              ＋ {tr('ip.new', 'Registrar IP')}
            </button>
          ) : undefined,
        }}

        actions={{
          extra: (ip) => {
            const asgn = assignmentByIp[ip.ip];
            const subnet = subnetMap[String(ip.subnet_id)];
            const ciPath = assignedPathOf(asgn);
            return (
              <>
                {/* Ver la ficha del equipo al que está asignada (?destacar, convención del CMDB) */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!ciPath}
                  onClick={() => ciPath && navigate(ciPath)}
                  title={asgn
                    ? `${tr('ip.see_assigned', 'Ver el equipo asignado')}: ${asgnNameOf(asgn)}`
                    : tr('ip.no_assigned', 'Esta IP no está asignada a ningún equipo')}
                >
                  <LinkIcon />
                </button>
                {/* Mapa de IPs de la subred */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!subnet}
                  onClick={() => navigate(explorerPath(subnet))}
                  title={tr('ip.view_in_explorer', 'Ver la subred en el Explorer')}
                >
                  <MagnifyingGlassIcon />
                </button>
              </>
            );
          },
          onEdit: openEdit,
          onDelete: handleDelete,
          deleteTitle: tr('ip.delete_title', 'Eliminar IP'),
          deleteMessage: (ip) => {
            const asgn = assignmentByIp[ip.ip];
            return (
              <>
                {tr('ip.delete_confirm', '¿Seguro que quieres eliminar la IP')} <strong>{ip.ip}</strong>?{' '}
                {asgn
                  ? <>{tr('ip.delete_assigned', 'Está asignada a')} <strong>{asgnNameOf(asgn)}</strong>: {tr('ip.delete_assigned_hint', 'esa asignación se perderá.')}</>
                  : tr('ip.delete_hint', 'Si alguna reserva DHCP o grupo VIP la usaba, se quedará sin IP.')}{' '}
                {tr('ip.delete_warn', 'No se puede deshacer.')}
              </>
            );
          },
        }}
      />

      {formOpen && (
        <IPForm
          ip={editingIp}
          mode={initialMode}
          subnets={subnets}
          saving={saving}
          onSave={handleSave}
          onClose={closeForm}
        />
      )}
    </IPAMLayout>
  );
}

/* ── Ficha / alta / edición de una IP ────────────────────────────────────────
   Un solo modal con tres modos: create (alta), view (lectura, lo que abre el
   clic en la fila) y edit. La IP y la subred no se pueden cambiar una vez
   creada: el endpoint de edición solo acepta tipo, estado, DNS y comentarios.
────────────────────────────────────────────────────────────────────────────── */
function IPForm({ ip, mode = 'create', subnets = [], saving, onSave, onClose }) {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };
  const isExisting = ip?.id != null;
  const [editing, setEditing] = useState(mode !== 'view');
  const [form, setForm] = useState({
    ip:        ip?.ip        || '',
    subnet_id: ip?.subnet_id != null ? String(ip.subnet_id) : '',
    type:      ip?.type      || 'static',
    status:    ip?.status    || 'free',
    dns_name:  ip?.dns_name  || '',
    comments:  ip?.comments  || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div
      className="modal d-block"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1060 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 560 }}>
        <div className="modal-content shadow-lg">
          <div className="modal-header py-2">
            <h6 className="modal-title fw-semibold mb-0">
              {!isExisting
                ? tr('ip.new', 'Registrar IP')
                : (editing ? tr('ip.edit', 'Editar IP') : tr('ip.view', 'Dirección IP'))}
              {isExisting && <span className="list-code ms-2">{ip.ip}</span>}
            </h6>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={handleSubmit}>
            <fieldset disabled={!editing} className="dv-fieldset">
              <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label className="form-label small mb-1">IP</label>
                  <input className="form-control form-control-sm list-code" placeholder="10.0.1.10" required
                    disabled={isExisting}
                    value={form.ip} onChange={e => setForm(f => ({ ...f, ip: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label small mb-1">{tr('ipam.subnet', 'Subred')}</label>
                  <select className="form-select form-select-sm" disabled={isExisting}
                    value={form.subnet_id} onChange={e => setForm(f => ({ ...f, subnet_id: e.target.value }))}>
                    <option value="">{tr('ip.subnet_auto', 'Auto (por la IP)')}</option>
                    {subnets.map(s => <option key={s.id} value={s.id}>{s.cidr}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label small mb-1">{tr('ipam.type', 'Tipo')}</label>
                  <select className="form-select form-select-sm" value={form.type}
                    onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                    {Object.entries(TYPE_META).map(([value, meta]) => (
                      <option key={value} value={value}>{meta.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label small mb-1">{tr('ipam.status', 'Estado')}</label>
                  <select className="form-select form-select-sm" value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="free">{tr('ip.status_free', 'Libre')}</option>
                    <option value="reserved">{tr('ip.status_reserved', 'Reservada')}</option>
                    {isExisting && <option value="assigned">{tr('ip.status_assigned', 'Asignada')}</option>}
                    {isExisting && <option value="conflict">{tr('ip.status_conflict', 'Conflicto')}</option>}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label small mb-1">DNS</label>
                  <input className="form-control form-control-sm list-code" placeholder="host.dominio.local"
                    value={form.dns_name} onChange={e => setForm(f => ({ ...f, dns_name: e.target.value }))} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label small mb-1">{tr('ip.comments', 'Comentarios')}</label>
                  <textarea className="form-control form-control-sm" rows={2}
                    value={form.comments} onChange={e => setForm(f => ({ ...f, comments: e.target.value }))} />
                </div>

                {isExisting && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <span className="list-hint">
                      {tr('ip.immutable_hint', 'La dirección y la subred no se pueden cambiar: si te has equivocado, registra la correcta y borra esta.')}
                    </span>
                  </div>
                )}
              </div>
            </fieldset>
            <div className="modal-footer py-2">
              {editing ? (
                <>
                  <button type="button" className="list-btn-secondary" onClick={onClose}>
                    {tr('action.cancel', 'Cancelar')}
                  </button>
                  <button type="submit" className="list-btn-primary" disabled={saving}>
                    {saving ? tr('msg.saving', 'Guardando…') : tr('action.save', 'Guardar')}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="list-btn-secondary" onClick={onClose}>
                    {tr('action.close', 'Cerrar')}
                  </button>
                  <button type="button" className="list-btn-primary" onClick={() => setEditing(true)}>
                    {tr('action.edit', 'Editar')}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
